"use client";

import { c as _c } from "react/compiler-runtime";
import { useContext, useState } from "react";
import { ApiIdentity } from "@/generated/models/ApiIdentity";
import { ApiProfileProxy } from "@/generated/models/ApiProfileProxy";
import { ApiProfileProxyAction } from "@/generated/models/ApiProfileProxyAction";
import { AuthContext } from "@/components/auth/Auth";
import { ReactQueryWrapperContext } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { useMutation } from "@tanstack/react-query";
import { AcceptActionRequest, AcceptActionRequestActionEnum } from "@/generated/models/AcceptActionRequest";
import { commonApiPost } from "@/services/api/common-api";
import CircleLoader from "@/components/distribution-plan-tool/common/CircleLoader";
import { haveSeenProfileProxyActionAcceptanceModal, setSeenProfileProxyActionAcceptanceModal } from "@/helpers/profile-proxy.helpers";
import HeaderProxyNewModal from "@/components/header/proxy/HeaderProxyNewModal";
import CommonAnimationWrapper from "@/components/utils/animation/CommonAnimationWrapper";
import CommonAnimationOpacity from "@/components/utils/animation/CommonAnimationOpacity";
const ACTION_LABEL: Record<AcceptActionRequestActionEnum, string> = {
  [AcceptActionRequestActionEnum.Accept]: "Accept",
  [AcceptActionRequestActionEnum.Reject]: "Reject",
  [AcceptActionRequestActionEnum.Revoke]: "Revoke",
  [AcceptActionRequestActionEnum.Restore]: "Restore"
};
const ACTION_CLASSES: Record<AcceptActionRequestActionEnum, string> = {
  [AcceptActionRequestActionEnum.Accept]: "tw-text-green",
  [AcceptActionRequestActionEnum.Reject]: "tw-text-red",
  [AcceptActionRequestActionEnum.Revoke]: "tw-text-red",
  [AcceptActionRequestActionEnum.Restore]: "tw-text-green"
};
export default function ProxyActionAcceptanceButton(t0) {
  const $ = _c(62);
  const {
    action,
    profile,
    profileProxy
  } = t0;
  const {
    setToast,
    connectedProfile,
    requestAuth
  } = useContext(AuthContext);
  const {
    onProfileProxyModify
  } = useContext(ReactQueryWrapperContext);
  let t1;
  if ($[0] !== action || $[1] !== connectedProfile || $[2] !== profile || $[3] !== profileProxy) {
    const getPossibleActions = () => {
      if (!connectedProfile?.id) {
        return [];
      }
      if (connectedProfile.id !== profile.id) {
        return [];
      }
      if (connectedProfile.id === profileProxy.created_by.id) {
        if (action.revoked_at) {
          return [AcceptActionRequestActionEnum.Restore];
        }
        return [AcceptActionRequestActionEnum.Revoke];
      }
      if (connectedProfile.id === profileProxy.granted_to.id) {
        if (!action.accepted_at && !action.rejected_at) {
          return [AcceptActionRequestActionEnum.Accept, AcceptActionRequestActionEnum.Reject];
        } else {
          if (action.accepted_at) {
            return [AcceptActionRequestActionEnum.Reject];
          } else {
            if (action.rejected_at) {
              return [AcceptActionRequestActionEnum.Accept];
            }
          }
        }
      }
      return [];
    };
    t1 = getPossibleActions();
    $[0] = action;
    $[1] = connectedProfile;
    $[2] = profile;
    $[3] = profileProxy;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const possibleActions = t1;
  const [submitting, setSubmitting] = useState(false);
  const [showAcceptanceModal, setShowAcceptanceModal] = useState(false);
  let t2;
  if ($[5] !== connectedProfile) {
    t2 = setAsDontShowAgain => {
      setShowAcceptanceModal(false);
      if (setAsDontShowAgain && connectedProfile?.id) {
        setSeenProfileProxyActionAcceptanceModal({
          profileId: connectedProfile.id
        });
      }
    };
    $[5] = connectedProfile;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const onAcceptanceModalClose = t2;
  let t3;
  if ($[7] !== connectedProfile) {
    t3 = () => {
      if (!connectedProfile?.id) {
        return;
      }
      const haveSeenModal = haveSeenProfileProxyActionAcceptanceModal({
        profileId: connectedProfile.id
      });
      if (haveSeenModal) {
        return;
      }
      setShowAcceptanceModal(true);
    };
    $[7] = connectedProfile;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const onSuccessFullProxyAcceptance = t3;
  let t4;
  if ($[9] !== action || $[10] !== profileProxy) {
    t4 = async body => await commonApiPost({
      endpoint: `proxies/${profileProxy.id}/actions/${action.id}/acceptance`,
      body
    });
    $[9] = action;
    $[10] = profileProxy;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== onProfileProxyModify || $[13] !== onSuccessFullProxyAcceptance || $[14] !== profileProxy || $[15] !== setToast) {
    t5 = (_, variables) => {
      if (!profileProxy.granted_to?.handle || !profileProxy.created_by?.handle) {
        return;
      }
      onProfileProxyModify({
        profileProxyId: profileProxy.id,
        grantedToHandle: profileProxy.granted_to.handle,
        createdByHandle: profileProxy.created_by.handle
      });
      setToast({
        message: "Action status changed",
        type: "success"
      });
      if (variables.action === AcceptActionRequestActionEnum.Accept) {
        onSuccessFullProxyAcceptance();
      }
    };
    $[12] = onProfileProxyModify;
    $[13] = onSuccessFullProxyAcceptance;
    $[14] = profileProxy;
    $[15] = setToast;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== setToast) {
    t6 = error => {
      setToast({
        message: error as unknown as string,
        type: "error"
      });
    };
    $[17] = setToast;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setSubmitting(false);
    };
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t4 || $[21] !== t5 || $[22] !== t6) {
    t8 = {
      mutationFn: t4,
      onSuccess: t5,
      onError: t6,
      onSettled: t7
    };
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const profileProxyAcceptanceMutation = useMutation(t8);
  let t9;
  if ($[24] !== profileProxyAcceptanceMutation || $[25] !== requestAuth) {
    t9 = async actionType => {
      setSubmitting(true);
      const {
        success
      } = await requestAuth();
      if (!success) {
        setSubmitting(false);
        return;
      }
      await profileProxyAcceptanceMutation.mutateAsync({
        action: actionType
      });
    };
    $[24] = profileProxyAcceptanceMutation;
    $[25] = requestAuth;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const onSubmit = t9;
  let t10;
  if ($[27] !== onSubmit || $[28] !== possibleActions || $[29] !== submitting) {
    t10 = possibleActions.includes(AcceptActionRequestActionEnum.Accept) && <button onClick={() => onSubmit(AcceptActionRequestActionEnum.Accept)} disabled={submitting} type="button" className="tw-text-green tw-bg-transparent tw-px-3 tw-py-2 tw-text-sm tw-leading-5 tw-font-semibold tw-border-0 hover:tw-border-iron-800 tw-rounded-lg hover:tw-bg-iron-800 tw-transition tw-duration-300 tw-ease-out">{submitting ? <CircleLoader /> : "Accept"}</button>;
    $[27] = onSubmit;
    $[28] = possibleActions;
    $[29] = submitting;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== t10) {
    t11 = <div className="tw-col-span-1">{t10}</div>;
    $[31] = t10;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== onSubmit || $[34] !== possibleActions || $[35] !== submitting) {
    t12 = possibleActions.includes(AcceptActionRequestActionEnum.Reject) && <button onClick={() => onSubmit(AcceptActionRequestActionEnum.Reject)} type="button" disabled={submitting} className="tw-text-red tw-bg-transparent tw-px-3 tw-py-2 tw-text-sm tw-leading-5 tw-font-semibold tw-border-0 hover:tw-border-iron-800 tw-rounded-lg hover:tw-bg-iron-800 tw-transition tw-duration-300 tw-ease-out">{submitting ? <CircleLoader /> : "Reject"}</button>;
    $[33] = onSubmit;
    $[34] = possibleActions;
    $[35] = submitting;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== onSubmit || $[38] !== possibleActions || $[39] !== submitting) {
    t13 = possibleActions.includes(AcceptActionRequestActionEnum.Revoke) && <button onClick={() => onSubmit(AcceptActionRequestActionEnum.Revoke)} disabled={submitting} type="button" className="tw-text-red tw-bg-transparent tw-px-3 tw-py-2 tw-text-sm tw-leading-5 tw-font-semibold tw-border-0 hover:tw-border-iron-800 tw-rounded-lg hover:tw-bg-iron-800 tw-transition tw-duration-300 tw-ease-out">{submitting ? <CircleLoader /> : "Revoke"}</button>;
    $[37] = onSubmit;
    $[38] = possibleActions;
    $[39] = submitting;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== onSubmit || $[42] !== possibleActions || $[43] !== submitting) {
    t14 = possibleActions.includes(AcceptActionRequestActionEnum.Restore) && <button onClick={() => onSubmit(AcceptActionRequestActionEnum.Restore)} disabled={submitting} type="button" className="tw-text-iron-300 tw-bg-transparent tw-px-3 tw-py-2 tw-text-sm tw-leading-5 tw-font-semibold tw-border-0 hover:tw-border-iron-800 tw-rounded-lg hover:tw-bg-iron-800 tw-transition tw-duration-300 tw-ease-out">{submitting ? <CircleLoader /> : "Restore"}</button>;
    $[41] = onSubmit;
    $[42] = possibleActions;
    $[43] = submitting;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== t12 || $[46] !== t13 || $[47] !== t14) {
    t15 = <div className="tw-col-span-1">{t12}{t13}{t14}</div>;
    $[45] = t12;
    $[46] = t13;
    $[47] = t14;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  let t16;
  if ($[49] !== t11 || $[50] !== t15) {
    t16 = <div className="tw-flex md:tw-justify-end"><div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-y-2 tw-gap-x-2">{t11}{t15}</div></div>;
    $[49] = t11;
    $[50] = t15;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  let t17;
  if ($[52] !== connectedProfile || $[53] !== onAcceptanceModalClose || $[54] !== profileProxy || $[55] !== showAcceptanceModal) {
    t17 = showAcceptanceModal && !!connectedProfile && <CommonAnimationOpacity key="modal" elementClasses="tw-absolute tw-z-10" elementRole="dialog" onClicked={_temp}><HeaderProxyNewModal onClose={onAcceptanceModalClose} connectedProfile={connectedProfile} proxyGrantor={profileProxy.created_by} /></CommonAnimationOpacity>;
    $[52] = connectedProfile;
    $[53] = onAcceptanceModalClose;
    $[54] = profileProxy;
    $[55] = showAcceptanceModal;
    $[56] = t17;
  } else {
    t17 = $[56];
  }
  let t18;
  if ($[57] !== t17) {
    t18 = <CommonAnimationWrapper mode="sync" initial={true}>{t17}</CommonAnimationWrapper>;
    $[57] = t17;
    $[58] = t18;
  } else {
    t18 = $[58];
  }
  let t19;
  if ($[59] !== t16 || $[60] !== t18) {
    t19 = <>{t16}{t18}</>;
    $[59] = t16;
    $[60] = t18;
    $[61] = t19;
  } else {
    t19 = $[61];
  }
  return t19;
}
function _temp(e) {
  return e.stopPropagation();
}